import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Radio,
  RadioGroup,
  FormGroup,
  Collapse,
  makeStyles,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
  countSelectedSports,
  getSportFieldKeys,
  isPartnerPerCategory,
} from './config';

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 4,
    color: '#1a3a5c',
  },
  subtitle: {
    textAlign: 'center',
    color: '#5a7a9a',
    marginBottom: 8,
  },
  counter: {
    textAlign: 'center',
    color: '#1a3a5c',
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    fontSize: '0.85rem',
  },
  optionCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    transition: 'all 0.2s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#bbb',
    },
  },
  optionCardSelected: {
    border: '2px solid #1a3a5c',
    backgroundColor: '#f5f8fc',
  },
  subField: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chipRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  chip: {
    minWidth: 42,
    height: 36,
    borderRadius: 8,
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: '#1a3a5c',
    },
  },
  chipSelected: {
    backgroundColor: '#1a3a5c',
    color: '#fff',
    borderColor: '#1a3a5c',
  },
  categoryLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: 4,
    color: '#333',
  },
  userCategory: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
    color: '#555',
    fontSize: '0.85rem',
  },
  warningBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fff5ee',
    border: '1px solid #ffe0cc',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(2),
    color: '#cc6600',
  },
}));

export default function StepModalidades({ formData, updateNestedFormData, activityConfig }) {
  const classes = useStyles();
  const m = formData.modalities;
  const sports = activityConfig?.sports || [];
  const sportLimit = activityConfig?.sportsLimit || 3;
  const optOutSport = sports.find((sport) => sport.special_type === 'opt_out');
  const optOutSelected = optOutSport ? !!m[optOutSport.id] : false;

  const sportCount = countSelectedSports(m, sports);

  const handleToggle = (sport) => {
    if (sport.special_type === 'opt_out') {
      sports.forEach((item) => updateNestedFormData('modalities', item.id, false));
      updateNestedFormData('modalities', sport.id, !m[sport.id]);
    } else {
      if (optOutSport && optOutSelected) {
        updateNestedFormData('modalities', optOutSport.id, false);
      }
      updateNestedFormData('modalities', sport.id, !m[sport.id]);
    }
  };

  const needsValidation = () => {
    return sports.some((sport) => {
      if (!m[sport.id]) return false;

      const keys = getSportFieldKeys(sport);
      if (sport.shirt.enabled && sport.shirt.required && !m[keys.shirt]) return true;

      if (sport.categories.enabled && sport.categories.required) {
        const categoriesValue = m[keys.categories];
        if (sport.categories.multiple) {
          if (!Array.isArray(categoriesValue) || categoriesValue.length === 0) {
            return true;
          }
        } else if (!categoriesValue) {
          return true;
        }
      }

      if (sport.partner_name.enabled && sport.partner_name.required) {
        const partnerValue = m[keys.partner];
        if (isPartnerPerCategory(sport)) {
          const selectedCategories = Array.isArray(m[keys.categories])
            ? m[keys.categories]
            : [];
          if (selectedCategories.length === 0) return true;
          const missing = selectedCategories.some(
            (category) =>
              !partnerValue ||
              typeof partnerValue !== 'object' ||
              !partnerValue[category]
          );
          if (missing) return true;
        } else if (!partnerValue) {
          return true;
        }
      }

      return false;
    });
  };

  const renderPartnerInputsPerCategory = (sport, selectedCategories) => {
    const keys = getSportFieldKeys(sport);
    if (!isPartnerPerCategory(sport) || selectedCategories.length === 0) {
      return null;
    }

    const partnerValue =
      m[keys.partner] && typeof m[keys.partner] === 'object'
        ? m[keys.partner]
        : {};
    const requiredMark = sport.partner_name.required ? ' *' : '';

    return (
      <Box mt={1}>
        {selectedCategories.map((category, idx) => (
          <TextField
            key={category}
            fullWidth
            variant="outlined"
            size="small"
            label={`${sport.partner_name.label} - ${category}${requiredMark}`}
            value={partnerValue[category] || ''}
            onChange={(e) =>
              updateNestedFormData('modalities', keys.partner, {
                ...partnerValue,
                [category]: e.target.value,
              })
            }
            style={{ marginTop: idx === 0 ? 0 : 8 }}
          />
        ))}
      </Box>
    );
  };

  const renderCategoriesField = (sport) => {
    const keys = getSportFieldKeys(sport);

    if (!sport.categories.enabled) return null;

    if (sport.categories.multiple) {
      const currentValue = Array.isArray(m[keys.categories]) ? m[keys.categories] : [];
      const maxSelection = sport.categories.maxSelection || 2;
      const limitReached = currentValue.length >= maxSelection;
      return (
        <>
          <Typography className={classes.categoryLabel}>
            Categoria * (selecione até {maxSelection})
          </Typography>
          <FormGroup>
            {sport.categories.options.map((category) => {
              const isChecked = currentValue.includes(category);
              const isDisabled = !isChecked && limitReached;
              return (
                <FormControlLabel
                  key={category}
                  disabled={isDisabled}
                  control={
                    <Checkbox
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => {
                        let updated;
                        if (isChecked) {
                          updated = currentValue.filter((item) => item !== category);
                        } else {
                          if (limitReached) return;
                          updated = [...currentValue, category];
                        }
                        updateNestedFormData(
                          'modalities',
                          keys.categories,
                          updated
                        );
                        if (isPartnerPerCategory(sport) && isChecked) {
                          const partnerValue =
                            m[keys.partner] &&
                            typeof m[keys.partner] === 'object'
                              ? { ...m[keys.partner] }
                              : {};
                          delete partnerValue[category];
                          updateNestedFormData(
                            'modalities',
                            keys.partner,
                            partnerValue
                          );
                        }
                      }}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{category}</Typography>}
                />
              );
            })}
          </FormGroup>
          {renderPartnerInputsPerCategory(sport, currentValue)}
        </>
      );
    }

    return (
      <>
        <Typography className={classes.categoryLabel}>Categoria *</Typography>
        <RadioGroup
          value={m[keys.categories] || ''}
          onChange={(e) => updateNestedFormData('modalities', keys.categories, e.target.value)}
        >
          {sport.categories.options.map((category) => (
            <FormControlLabel
              key={category}
              value={category}
              control={<Radio color="primary" size="small" />}
              label={<Typography variant="body2">{category}</Typography>}
            />
          ))}
        </RadioGroup>
      </>
    );
  };

  const renderOption = (sport) => {
    const keys = getSportFieldKeys(sport);
    const checked = m[sport.id];
    const disabled =
      !checked &&
      sport.special_type !== 'opt_out' &&
      sportCount >= sportLimit &&
      !optOutSelected;

    const children = (
      <>
        {sport.shirt.enabled && (
          <>
            <Typography className={classes.categoryLabel}>Tamanho da Camisa *</Typography>
            <Box className={classes.chipRow}>
              {sport.shirt.sizes.map((size) => (
                <Box
                  key={size}
                  className={`${classes.chip} ${m[keys.shirt] === size ? classes.chipSelected : ''}`}
                  onClick={() => updateNestedFormData('modalities', keys.shirt, size)}
                >
                  {size}
                </Box>
              ))}
            </Box>
          </>
        )}

        {renderCategoriesField(sport)}

        {sport.partner_name.enabled && !isPartnerPerCategory(sport) && (
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={sport.partner_name.label}
            value={typeof m[keys.partner] === 'string' ? m[keys.partner] : ''}
            onChange={(e) => updateNestedFormData('modalities', keys.partner, e.target.value)}
            style={{ marginTop: sport.categories.enabled || sport.shirt.enabled ? 8 : 0 }}
          />
        )}
      </>
    );

    return (
      <Box
        className={`${classes.optionCard} ${checked ? classes.optionCardSelected : ''}`}
        key={sport.id}
        onClick={() => !disabled && !checked && handleToggle(sport)}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={() => handleToggle(sport)}
              disabled={disabled}
              color="primary"
              style={{ color: checked ? '#1a3a5c' : undefined }}
            />
          }
          label={
            <Typography variant="body1" style={{ fontWeight: checked ? 600 : 400 }}>
              {sport.name}
            </Typography>
          }
          onClick={(e) => e.stopPropagation()}
        />
        {(sport.shirt.enabled || sport.categories.enabled || sport.partner_name.enabled) && (
          <Collapse in={checked}>
            <Box className={classes.subField} onClick={(e) => e.stopPropagation()}>
              {children}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h5" className={classes.title}>
        Modalidades Esportivas
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Selecione até {activityConfig?.sportsLimit || 3} modalidades para participar
      </Typography>
      {sportCount > 0 && (
        <Typography className={classes.counter}>
          {sportCount}/{activityConfig?.sportsLimit || 3} modalidades selecionadas
        </Typography>
      )}

      {sports.map((sport) => renderOption(sport))}

      {needsValidation() && (
        <Box className={classes.warningBar}>
          <InfoOutlinedIcon style={{ fontSize: 20 }} />
          <Typography variant="body2">
            Preencha todos os campos obrigatórios das modalidades selecionadas
          </Typography>
        </Box>
      )}
    </Box>
  );
}
